import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, qs, type PutAwayRowData } from '../api';
import { ColorSwatch, ItemImage } from '../components/ItemImage';
import { useLocations } from '../queries';
import { Badge, Button, Card, Empty, ErrorMessage, Input, Notice, PageHeader, Select, Spinner, number, plural } from '../ui';

const COND = { N: 'New', U: 'Used' } as const;

/**
 * Stock that has arrived but has no bin yet. Each part comes with a suggested bin: one that already holds the same
 * part, colour and condition (the fullest), else the colour's usual bin. Change any you like, then put them away.
 */
export function PutAway() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const purchaseId = params.get('purchaseId');
  const locations = useLocations();
  const [chosen, setChosen] = useState<Record<number, string>>({});
  const [filter, setFilter] = useState('');
  const [done, setDone] = useState<number | null>(null);

  const list = useQuery({
    queryKey: ['put-away', purchaseId],
    queryFn: () => api<{ rows: PutAwayRowData[]; total: number }>(`/api/v1/put-away${qs({ purchaseId })}`),
  });
  // Each row starts on its suggested bin.
  useEffect(() => {
    if (list.data) setChosen(Object.fromEntries(list.data.rows.filter((r) => r.suggestion).map((r) => [r.entryId, String(r.suggestion!.locationId)])));
  }, [list.data]);

  const rows = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (list.data?.rows ?? []).filter((r) => !f || `${r.itemNo} ${r.itemName} ${r.colorName}`.toLowerCase().includes(f));
  }, [list.data, filter]);
  const moves = (list.data?.rows ?? []).filter((r) => chosen[r.entryId]).map((r) => ({ entryId: r.entryId, locationId: Number(chosen[r.entryId]) }));

  const put = useMutation({
    mutationFn: () => api<{ result: { moved: number } }>('/api/v1/put-away', { body: { moves } }).then((r) => r.result.moved),
    onSuccess: (moved) => {
      setDone(moved);
      for (const k of ['put-away', 'lots', 'dashboard', 'locations', 'item-inventory']) void qc.invalidateQueries({ queryKey: [k] });
    },
  });

  return (
    <>
      <PageHeader
        title="Put away"
        subtitle={
          purchaseId ? (
            <>
              Stock from{' '}
              <Link to={`/purchases/${purchaseId}`} className="underline">
                purchase {purchaseId}
              </Link>{' '}
              that has no bin yet.{' '}
              <Link to="/put-away" className="underline">
                Show everything
              </Link>
            </>
          ) : (
            'Stock that has arrived but has no bin yet. Parts go where the same part, colour and condition already is.'
          )
        }
      />
      <div className="space-y-4">
        {done !== null && <Notice tone="green">Put away {plural(done, 'line')}.</Notice>}
        <ErrorMessage error={put.error} />
        {list.isPending ? (
          <Spinner />
        ) : list.error ? (
          <ErrorMessage error={list.error} />
        ) : list.data.rows.length === 0 ? (
          <Empty>Nothing to put away. Every piece in stock has a bin.</Empty>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <Input aria-label="Filter" className="w-60" placeholder="Filter parts" value={filter} onChange={(e) => setFilter(e.target.value)} />
              <span className="text-sm text-slate-600">
                {number(moves.length)} of {number(list.data.rows.length)} have a bin chosen
                {list.data.total > list.data.rows.length && ` (showing the first ${number(list.data.rows.length)} of ${number(list.data.total)})`}
              </span>
              <Button className="ml-auto" disabled={moves.length === 0 || put.isPending} onClick={() => put.mutate()}>
                {put.isPending ? 'Putting away…' : `Put away ${plural(moves.length, 'line')}`}
              </Button>
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
              <table className="data w-full min-w-[56rem] text-sm">
                <thead>
                  <tr>
                    <th className="min-w-14" />
                    <th>Part</th>
                    <th>Colour</th>
                    <th>Cond.</th>
                    <th className="text-right">Pieces</th>
                    <th>From</th>
                    <th>Bin</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const suggested = new Set([r.suggestion?.locationId, ...r.alternatives.map((a) => a.locationId)].filter((x): x is number => x != null));
                    return (
                      <tr key={r.entryId}>
                        <td className="min-w-14">
                          <ItemImage itemType={r.itemType} itemNo={r.itemNo} colorId={r.colorId} />
                        </td>
                        <td>
                          <Link to={`/lots/${r.lotId}`} className="font-medium hover:underline">
                            {r.itemNo}
                          </Link>
                          <div className="max-w-64 truncate text-xs text-slate-500">{r.itemName}</div>
                        </td>
                        <td className="whitespace-nowrap">
                          <ColorSwatch rgb={r.colorRgb} name={r.colorName} />
                        </td>
                        <td>{COND[r.condition]}</td>
                        <td className="text-right tabular-nums">{number(r.qty)}</td>
                        <td className="max-w-48 truncate text-xs text-slate-500">
                          <Link to={`/purchases/${r.purchaseId}`} className="hover:underline">
                            {r.source || `Purchase ${r.purchaseId}`}
                          </Link>
                        </td>
                        <td>
                          <Select
                            aria-label={`Bin for ${r.itemNo} ${r.colorName} ${COND[r.condition].toLowerCase()}`}
                            className="w-40"
                            value={chosen[r.entryId] ?? ''}
                            onChange={(e) => setChosen({ ...chosen, [r.entryId]: e.target.value })}
                          >
                            <option value="">Not yet</option>
                            {r.suggestion && (
                              <option value={r.suggestion.locationId}>
                                {r.suggestion.code} · {r.suggestion.reason === 'holds' ? `holds ${number(r.suggestion.qtyThere)}` : 'usual bin'}
                              </option>
                            )}
                            {r.alternatives.map((a) => (
                              <option key={a.locationId} value={a.locationId}>
                                {a.code} · holds {number(a.qtyThere)}
                              </option>
                            ))}
                            {locations.data?.filter((l) => !suggested.has(l.id)).map((l) => (
                              <option key={l.id} value={l.id}>
                                {l.code}
                              </option>
                            ))}
                          </Select>
                          {r.suggestion ? (
                            <div className="mt-0.5">
                              <Badge tone={r.suggestion.reason === 'holds' ? 'green' : 'blue'}>{r.suggestion.reason === 'holds' ? 'with the same part' : 'usual bin'}</Badge>
                            </div>
                          ) : (
                            <div className="mt-0.5 text-xs text-amber-700">No bin holds this yet</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}
